import { useState, useCallback } from 'react';
import { apiPost, ApiResponse, ApiError } from '../../services/api.service';
import type { Extra } from '../../types';

interface UseUpdateCodeOfConductReturn {
  updateCodeOfConduct: (accepted: boolean) => Promise<boolean>;
  loading: boolean;
  error: string | null;
  success: boolean;
}

/**
 * Custom hook to update code of conduct status for an extra
 * @param extraId - The ID of the extra (defaults to 4)
 * @returns Function to update code of conduct status, loading state, error state, and success flag
 */
export function useUpdateCodeOfConduct(extraId?: number | string): UseUpdateCodeOfConductReturn {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const updateCodeOfConduct = useCallback(async (accepted: boolean): Promise<boolean> => {
    // Return early if no extraId is provided
    if (!extraId) {
      setError('No user ID available');
      return false;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const response: ApiResponse<Extra> = await apiPost<Extra>(
        `/extras/${extraId}/code-of-conduct`,
        { accepted },
        {
          notification: {
            enabled: true,
            showSuccess: true,
            showError: true,
            successMessage: accepted ? 'Code of conduct accepted successfully' : 'Code of conduct declined',
            errorMessage: 'Failed to update code of conduct status'
          }
        }
      );

      if (response.success) {
        setSuccess(true);
        return true;
      } else {
        setError(response.error || 'Failed to update code of conduct status');
        return false;
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [extraId]);

  return {
    updateCodeOfConduct,
    loading,
    error,
    success,
  };
}

// Backward compatibility export
export const useAcceptCodeOfConduct = useUpdateCodeOfConduct;